import React from "react";
import {
  SingleTripSelectedAvnIcon,
  SingleTripNonSelectAvnIcon,
  AnnualMultiTripSelectedAvnIcon,
  AnnualMultiTripNonSelectAvnIcon,
  SingleTripSelectedStsIcon,
  SingleTripNonSelectStsIcon,
  AnnualMultiTripSelectedStsIcon,
  AnnualMultiTripNonSelectStsIcon,
} from "staysure-component-library";

import {
  ANNUAL_MULTI_TRIP,
  BRAND_AVANTI,
  BRAND_STAYSURE,
  BRAND_EXPAT,
  SINGLE_TRIP,
} from "@/constants";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";

export function calculateAmtBasicPriceDifference(
  policyPackagePrices: PolicyPackagePrice[] | undefined
): string | null {
  if (!policyPackagePrices || policyPackagePrices.length === 0) {
    return null;
  }
  const amtBasicPackage = policyPackagePrices.find(
    (price) =>
      price.policyType === ANNUAL_MULTI_TRIP && price.policyPackage === "BASIC"
  );

  if (amtBasicPackage?.diff !== null && amtBasicPackage?.diff !== undefined) {
    return amtBasicPackage.diff.toFixed(2);
  }

  return null;
}

/**
 * Calculate Annual Multi-Trip price difference for Avanti brand
 * based on product type (CRUISE uses CLASSIC package, others use ESSENTIALS)
 */
export function calculateAmtPriceDifferenceForAvanti(
  policyPackagePrices: PolicyPackagePrice[] | undefined,
  product: string | undefined
): string | null {
  if (!policyPackagePrices || policyPackagePrices.length === 0) {
    return null;
  }

  const packageName = product === "CRUISE" ? "CLASSIC" : "ESSENTIALS";

  const match = policyPackagePrices.find(
    (price) =>
      price.policyType === ANNUAL_MULTI_TRIP &&
      price.policyPackage === packageName
  );

  if (match?.diff !== null && match?.diff !== undefined) {
    return match.diff.toFixed(2);
  }

  return null;
}

/**
 * Brand-specific price calculation strategies
 */
type PriceCalculationStrategy = (
  policyPackagePrices: PolicyPackagePrice[] | undefined,
  product?: string | undefined
) => string | null;

const priceCalculationStrategies: Record<string, PriceCalculationStrategy> = {
  [BRAND_AVANTI]: calculateAmtPriceDifferenceForAvanti,
  [BRAND_STAYSURE]: calculateAmtBasicPriceDifference,
};

export function calculateAmtPriceDifferenceByBrand(
  brand: string,
  policyPackagePrices: PolicyPackagePrice[] | undefined,
  product?: string | undefined
): string | null {
  const strategy = priceCalculationStrategies[brand];
  if (!strategy) {
    // Fallback to default calculation if brand not found
    return calculateAmtBasicPriceDifference(policyPackagePrices);
  }

  return strategy(policyPackagePrices, product);
}

/**
 * Configuration for button tabs (Single Trip and Annual Multi-Trip)
 */
export interface ButtonTab {
  id: string;
  label: string;
  className: string;
  value: string;
}

export function createBtnTabs(
  topToggleBtnOneLabel: string,
  topToggleBtnTwoLabel: string
): ButtonTab[] {
  return [
    {
      id: "SINGLE_TRIP",
      label: topToggleBtnOneLabel,
      className:
        "w-[134px] h-[36px] leading-[24px] disabled:cursor-not-allowed",
      value: SINGLE_TRIP,
    },
    {
      id: "ANNUAL_MULTI_TRIP",
      label: topToggleBtnTwoLabel,
      className:
        "w-[134px] h-[36px] leading-[24px] disabled:cursor-not-allowed",
      value: ANNUAL_MULTI_TRIP,
    },
  ];
}

/**
 * Check if a date string represents today's date
 */
export function isToday(dateString: string): boolean {
  const backendDate = new Date(dateString);
  const today = new Date();

  return (
    backendDate.getFullYear() === today.getFullYear() &&
    backendDate.getMonth() === today.getMonth() &&
    backendDate.getDate() === today.getDate()
  );
}

/**
 * Check if current policy type has only one cover eligible
 * A cover is considered eligible if it's not medically declined
 *
 * @param policyType - The policy type to check (SINGLE_TRIP or ANNUAL_MULTI_TRIP)
 * @param policyPackagePrices - Array of policy package prices from API
 * @returns true if only one cover is eligible for the given policy type, false otherwise
 */
export function hasOnlyOneCoverEligible(
  policyType?: string,
  policyPackagePrices?: PolicyPackagePrice[]
): boolean {
  if (!policyPackagePrices || policyPackagePrices.length === 0) {
    return false;
  }

  // Filter covers for the current policy type that are not declined
  const eligibleCovers = policyPackagePrices.filter(
    (price) =>
      price.policyType === policyType && price.hasMedicalDeclined !== true
  );

  return eligibleCovers.length === 1;
}

/**
 * Interface for trip type toggle handler dependencies
 */
export interface TripTypeToggleHandlerDeps {
  tripDetails:
    | {
        startDate?: string;
      }
    | null
    | undefined;
  selectedTab: string;
  TabToggleHandler: (tripType: string) => void;
  showLoading: () => void;
  setIsAmtModalOpen: (isOpen: boolean) => void;
  isPriceToggleEnabled: boolean;
}

/**
 * Creates a handler function for trip type toggle
 * Handles the logic for switching between Single Trip and Annual Multi-Trip
 */
export function createTripTypeToggleHandler(
  deps: TripTypeToggleHandlerDeps
): (val: string | number) => void {
  const {
    tripDetails,
    selectedTab,
    TabToggleHandler,
    showLoading,
    setIsAmtModalOpen,
    isPriceToggleEnabled,
  } = deps;

  return (val: string | number) => {
    const tripType = String(val);

    // If price toggle experiment is enabled, skip modal for ST -> AMT switch
    if (isPriceToggleEnabled) {
      // If switching to AMT from ST, switch immediately without showing modal
      if (tripType === ANNUAL_MULTI_TRIP && selectedTab === SINGLE_TRIP) {
        TabToggleHandler(tripType);
        showLoading();
        document.getElementById(tripType)?.blur();
      }
      // If switching from AMT to ST, switch immediately
      else if (tripType === SINGLE_TRIP && selectedTab === ANNUAL_MULTI_TRIP) {
        TabToggleHandler(tripType);
        showLoading();
        document.getElementById(tripType)?.blur();
      }
      return;
    }

    // Original behavior when experiment is not enabled
    // If switching to AMT from ST and start date is today, switch immediately
    if (
      tripDetails?.startDate &&
      isToday(tripDetails.startDate) &&
      tripType === ANNUAL_MULTI_TRIP &&
      selectedTab === SINGLE_TRIP
    ) {
      TabToggleHandler(tripType);
      showLoading();
      document.getElementById(tripType)?.blur();
    }
    // If switching to AMT from ST on a future date, show modal
    else if (tripType === ANNUAL_MULTI_TRIP && selectedTab === SINGLE_TRIP) {
      setIsAmtModalOpen(true);
    }
    // If switching from AMT to ST, switch immediately
    else if (tripType === SINGLE_TRIP && selectedTab === ANNUAL_MULTI_TRIP) {
      TabToggleHandler(tripType);
      showLoading();
      document.getElementById(tripType)?.blur();
    }
  };
}

/**
 * Brand-specific UI configuration for quote cover component
 * Centralizes brand-dependent UI elements to eliminate inline conditionals
 */
export interface BrandUIConfig {
  icons: {
    singleTrip: {
      selected: React.ComponentType<{ width: number; height: number }>;
      nonSelected: React.ComponentType<{ width: number; height: number }>;
    };
    annualMultiTrip: {
      selected: React.ComponentType<{ width: number; height: number }>;
      nonSelected: React.ComponentType<{ width: number; height: number }>;
    };
  };
  features: {
    enableExtendedCarousel: boolean;
    enableBadgeFeature: boolean;
  };
}

/**
 * Brand UI configuration lookup
 * Maps brand keys to their specific UI configurations
 */
const brandUIConfig: Record<string, BrandUIConfig> = {
  [BRAND_AVANTI]: {
    icons: {
      singleTrip: {
        selected: SingleTripSelectedAvnIcon,
        nonSelected: SingleTripNonSelectAvnIcon,
      },
      annualMultiTrip: {
        selected: AnnualMultiTripSelectedAvnIcon,
        nonSelected: AnnualMultiTripNonSelectAvnIcon,
      },
    },
    features: {
      enableExtendedCarousel: false,
      enableBadgeFeature: false,
    },
  },
  [BRAND_STAYSURE]: {
    icons: {
      singleTrip: {
        selected: SingleTripSelectedStsIcon,
        nonSelected: SingleTripNonSelectStsIcon,
      },
      annualMultiTrip: {
        selected: AnnualMultiTripSelectedStsIcon,
        nonSelected: AnnualMultiTripNonSelectStsIcon,
      },
    },
    features: {
      enableExtendedCarousel: true,
      enableBadgeFeature: true,
    },
  },
  [BRAND_EXPAT]: {
    icons: {
      singleTrip: {
        selected: SingleTripSelectedStsIcon,
        nonSelected: SingleTripNonSelectStsIcon,
      },
      annualMultiTrip: {
        selected: AnnualMultiTripSelectedStsIcon,
        nonSelected: AnnualMultiTripNonSelectStsIcon,
      },
    },
    features: {
      enableExtendedCarousel: false,
      enableBadgeFeature: false,
    },
  },
};

/**
 * Get brand-specific UI configuration with fallback
 * @param brand - Brand key (staysure, avanti, expat)
 * @returns Brand UI configuration (defaults to Staysure if brand not found)
 */
export function getBrandUIConfig(brand: string): BrandUIConfig {
  return brandUIConfig[brand] || brandUIConfig[BRAND_STAYSURE];
}
